/**
 * useIdleState Hook
 * 
 * React hook for detecting and managing idle state
 * Requirements: 19.5
 */

import { useEffect, useState, useCallback } from 'react';
import { IdleStateManager, resourceCleanupManager } from '../utils/idleStateManager';

export interface UseIdleStateOptions {
  idleTimeout?: number; // milliseconds, default 5 minutes
  onIdle?: () => void;
  onActive?: () => void;
  enabled?: boolean;
}

export function useIdleState(options: UseIdleStateOptions = {}) {
  const {
    idleTimeout = 5 * 60 * 1000, // 5 minutes default
    onIdle,
    onActive,
    enabled = true,
  } = options;

  const [isIdle, setIsIdle] = useState(false);

  const handleIdle = useCallback(() => {
    console.log('Application entered idle state');
    setIsIdle(true);
    
    // Execute resource cleanup
    resourceCleanupManager.cleanup();
    
    // Call custom callback
    if (onIdle) {
      onIdle();
    }
  }, [onIdle]);

  const handleActive = useCallback(() => {
    console.log('Application became active');
    setIsIdle(false);
    
    // Call custom callback
    if (onActive) {
      onActive();
    }
  }, [onActive]);

  useEffect(() => {
    if (!enabled) return;

    const idleManager = new IdleStateManager({
      idleTimeout,
      onIdle: handleIdle,
      onActive: handleActive,
    });

    idleManager.start();

    return () => {
      idleManager.stop();
    };
  }, [idleTimeout, handleIdle, handleActive, enabled]);

  return { isIdle };
}

/**
 * useResourceCleanup Hook
 * 
 * Register a cleanup function to be called during idle state
 */
export function useResourceCleanup(cleanupFn: () => void, deps: React.DependencyList = []) {
  useEffect(() => {
    resourceCleanupManager.registerCleanup(cleanupFn);

    return () => {
      resourceCleanupManager.unregisterCleanup(cleanupFn);
    };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
}
